/**
 * Chat session / strategy builder (ARCHITECTURE.md §8) — the two-panel builder.
 *
 * Left: the streaming conversation (`@ai-sdk/react` `useChat` over the session's
 * SSE stream, tool-call aware). Right: the live strategy document, driven by the
 * streamed `edit_strategy` tool *input* (§6; legacy `build_strategy` /
 * `patch_strategy` parts in old histories still fold), editable field by field,
 * validity-checked against the real `StrategyDSLSchema`, and promotable into a
 * `strategies` row. The two are wired through `useBuilderDraft`, which folds
 * streamed tool input + operator edits into one draft and reconciles it to the
 * server (the source of truth for `chat_sessions.draft_dsl`).
 *
 * The whole subtree is keyed by session id so switching sessions re-seeds the
 * conversation and the draft cleanly rather than bleeding one into the next.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@richrich/ui";
import type { ChatSessionDetailResponse } from "@richrich/contracts";
import { useI18n } from "@richrich/i18n/react";
import { AsyncState } from "../../components/ui/AsyncState";
import { settingsQuery } from "../../lib/query";
import { chatSessionQuery } from "../../features/chat/api";
import { useResumeTurn } from "../../features/chat/resume";
import { useBuilderChat } from "../../features/chat/useBuilderChat";
import { useBuilderDraft } from "../../features/chat/useBuilderDraft";
import { Conversation } from "../../features/chat/components/Conversation";
import { DslDocument } from "../../features/chat/components/DslDocument";

export const Route = createFileRoute("/chat/$sessionId")({
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(chatSessionQuery(params.sessionId)),
      context.queryClient.ensureQueryData(settingsQuery),
    ]);
  },
  component: ChatSessionPage,
});

function ChatSessionPage() {
  const { t } = useI18n();
  const { sessionId } = Route.useParams();
  const detail = useQuery(chatSessionQuery(sessionId));
  const settings = useQuery(settingsQuery);
  const aiUnlocked = settings.data?.features.ai ?? false;

  return (
    <AsyncState
      query={detail}
      skeleton={<CenterNote>{t("chat.loadingSession")}</CenterNote>}
      error={
        <CenterNote tone="error">
          {t("chat.sessionLoadError")}{" "}
          <Link to="/chat" className="underline">
            {t("chat.backToSessions")}
          </Link>
        </CenterNote>
      }
    >
      {(data) => (
        <Builder key={sessionId} sessionId={sessionId} detail={data} aiUnlocked={aiUnlocked} />
      )}
    </AsyncState>
  );
}

function Builder({
  sessionId,
  detail,
  aiUnlocked,
}: {
  sessionId: string;
  detail: ChatSessionDetailResponse;
  aiUnlocked: boolean;
}) {
  const chat = useBuilderChat({ sessionId, history: detail.messages });
  const builder = useBuilderDraft({
    sessionId,
    serverDraft: detail.draftDsl,
    messages: chat.messages,
    status: chat.status,
  });

  // Lite resume (§6): while the server reports a turn still running for this
  // session and there is no live LOCAL stream, poll the detail GET and fold the
  // growing checkpointed history into the chat (content-driven merge — a
  // checkpoint row growing in place under the same id must re-render; see
  // `useResumeTurn`). Stops on finish/interruption/staleness or a new local turn.
  const resuming = useResumeTurn({
    sessionId,
    seedMessages: detail.messages,
    initialTurnInFlight: detail.turnInFlight,
    status: chat.status,
    setMessages: chat.setMessages,
  });

  return (
    <div className="flex h-full min-h-0 flex-col">
      <TopBar detail={detail} />
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <section className="flex min-h-0 flex-1 flex-col border-b border-hairline lg:border-b-0 lg:border-r">
          <Conversation chat={chat} locked={!aiUnlocked} resuming={resuming} />
        </section>
        <section className="flex min-h-0 flex-1 flex-col">
          <DslDocument sessionId={sessionId} builder={builder} />
        </section>
      </div>
    </div>
  );
}

function TopBar({ detail }: { detail: ChatSessionDetailResponse }) {
  const { t } = useI18n();
  return (
    <header className="flex items-center gap-3 border-b border-hairline px-4 py-2.5">
      <Link
        to="/chat"
        className="rounded-md px-1.5 py-1 text-sm text-muted transition-colors hover:text-ink"
        aria-label={t("chat.backToSessions")}
      >
        ←
      </Link>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h1 className="truncate text-sm font-semibold text-ink">
            {detail.title ?? t("chat.builderTitle")}
          </h1>
          {detail.strategyId !== null && <Badge tone="info">{t("chat.refining")}</Badge>}
        </div>
      </div>
      {detail.providerModel !== null && (
        <span className="hidden shrink-0 font-mono text-[11px] text-muted sm:inline">
          {detail.providerModel}
        </span>
      )}
    </header>
  );
}

function CenterNote({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "error";
}) {
  return (
    <div className="flex h-full items-center justify-center p-8">
      <p className={tone === "error" ? "text-sm text-critical" : "text-sm text-muted"}>
        {children}
      </p>
    </div>
  );
}
